/**
 * 📸 stationCamera — Brücke zum nativen Camera2-Aufnahmemodul (StationCamPlugin).
 *
 * Die WebView bekommt auf vielen Geräten (Samsung!) weder Hardware-Zoom noch die
 * Tele-Linsen. Das native Modul kann: echten optischen Zoom (CONTROL_ZOOM_RATIO),
 * volle Foto-Auflösung und Abstandsmessung über den Autofokus → automatisch die
 * optimale Brennweite für 1,2–1,8 m Etikett-Distanz.
 *
 * Alle Funktionen fallen sauber auf null zurück, wenn das Plugin fehlt
 * (Browser / alte APK) — die Station nutzt dann weiter den getUserMedia-Weg.
 *
 * WICHTIG: Vor nativeCapture() muss der getUserMedia-Stream gestoppt sein
 * (Camera2 braucht exklusiven Kamera-Zugriff).
 */
import { Capacitor, registerPlugin } from '@capacitor/core';

const StationCam = registerPlugin('StationCam');

export function nativeCamSupported() {
  return Capacitor.getPlatform() === 'android';
}

/**
 * Kamera-Berechtigung der APP prüfen und ggf. anfragen. Hintergrund: Ist sie in Android
 * verweigert, bricht Capacitor den <input capture>-Aufruf STILL ab — der Kamera-Knopf
 * tut dann scheinbar nichts. 'granted' | 'denied' | null (Plugin fehlt = alte APK/Browser).
 */
export async function nativeCameraPermission() {
  if (!Capacitor.isNativePlatform() || !nativeCamSupported()) return null;
  try {
    const st = await StationCam.checkPermissions();
    if (st && st.camera === 'granted') return 'granted';
    const r = await StationCam.requestPermissions({ permissions: ['camera'] });
    return r && r.camera === 'granted' ? 'granted' : 'denied';
  } catch { return null; }
}

/**
 * Mikrofon-Berechtigung der APP prüfen und ggf. anfragen (für die DHL-Scanner-Piep-Erkennung).
 */
export async function nativeMicPermission() {
  if (!Capacitor.isNativePlatform() || !nativeCamSupported()) return null;
  try {
    const st = await StationCam.checkPermissions();
    if (st && st.microphone === 'granted') return 'granted';
    const r = await StationCam.requestPermissions({ permissions: ['microphone'] });
    return r && r.microphone === 'granted' ? 'granted' : 'denied';
  } catch { return null; }
}

/** Kamera-Fähigkeiten nativ auslesen — null, wenn Plugin fehlt (alte APK). */
export async function nativeProbe() {
  if (!nativeCamSupported()) return null;
  try {
    const r = await StationCam.probe();
    return r && Array.isArray(r.cameras) ? r.cameras : null;
  } catch { return null; }
}

/**
 * Natives Foto: {jpegBase64, width, height, zoomRatio, distanceM, bytes} oder null.
 * opts: { autoZoom?: boolean, zoomRatio?: number, quality?: number }
 */
export async function nativeCapture(opts = {}) {
  if (!nativeCamSupported()) return null;
  try {
    const r = await StationCam.capture({ autoZoom: true, quality: 92, ...opts });
    return r && r.jpegBase64 ? r : { error: 'leeres Ergebnis' };
  } catch (e) { return { error: String(e?.message || e) }; }
}

/**
 * Display der Station dauerhaft anlassen (FLAG_KEEP_SCREEN_ON). Verhindert Dimmen/
 * Schlafen → keine Chromium-Timer-Drosselung (sonst kriecht das Auto-Auslösen).
 * No-op auf alten APKs ohne die Methode.
 */
export async function nativeKeepAwake(on) {
  if (!nativeCamSupported()) return false;
  try { await StationCam.setKeepAwake({ on: !!on }); return true; } catch { return false; }
}

/**
 * App-Fenster-Helligkeit setzen: 0.0–1.0, oder <0 für System-Automatik.
 * Nur diese App (nicht die System-Helligkeit); im Standby dunkel = weniger Wärme/Akku.
 * No-op auf alten APKs / im Browser.
 */
export async function nativeSetBrightness(level) {
  if (!nativeCamSupported()) return false;
  try { await StationCam.setBrightness({ level }); return true; } catch { return false; }
}

/** Kiosk/Lock-Task an/aus (Device-Owner → feste Sperre, sonst „Anheften"). */
export async function nativeKiosk(on) {
  if (!nativeCamSupported()) return false;
  try { const r = await StationCam.setKiosk({ on: !!on }); return !!(r && r.ok); } catch { return false; }
}

/** true, wenn die App von der Akku-Optimierung ausgenommen ist (läuft ungedrosselt). */
export async function nativeBatteryOptIgnored() {
  if (!nativeCamSupported()) return null;
  try { const r = await StationCam.isBatteryOptIgnored(); return !!(r && r.ignored); } catch { return null; }
}

/** Öffnet die App-Detail-Einstellungen (Akku → „Nicht eingeschränkt"). */
export async function nativeOpenBatteryOpt() {
  if (!nativeCamSupported()) return false;
  try { await StationCam.openBatteryOptSettings(); return true; } catch { return false; }
}
